import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import Avatar from '../components/Avatar';
import AgencyLogo from '../components/AgencyLogo';
import type { Flag as FlagType } from '../types';

const getStoredUser = () => {
  try { return JSON.parse(sessionStorage.getItem('iad2_user') || 'null'); } catch { return null; }
};

const timeAgo = (dateStr: string): string => {
  const d = new Date(dateStr);
  const days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

const FlagsPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = getStoredUser();
  const canResolve = currentUser?.role === 'Administrator' || currentUser?.role === 'Superuser';
  const qc = useQueryClient();

  const [tab, setTab] = useState<'open' | 'resolved' | 'all'>('open');
  const [resolveModalFlagId, setResolveModalFlagId] = useState<string | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['flags'],
    queryFn: () => api.getFlags({}),
  });

  const allFlags = (data?.data ?? []) as (FlagType & Record<string, any>)[];

  const st = (f: FlagType) => (f.status ?? '').toLowerCase();

  const openFlags     = allFlags.filter((f) => st(f) === 'open');
  const filteredFlags =
    tab === 'open'
      ? openFlags
      : tab === 'resolved'
      ? allFlags.filter((f) => ['resolved', 'dismissed'].includes(st(f)))
      : allFlags;

  const openCount = openFlags.length;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['flags'] });
    qc.invalidateQueries({ queryKey: ['kpi'] });
    window.dispatchEvent(new Event('iad2:counts-stale'));
  };

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      api.resolveFlag(id, { resolution_note: note }),
    onSuccess: () => {
      invalidate();
      setResolveModalFlagId(null);
      setResolutionNote('');
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.resolveFlag(id, { action: 'dismiss' }),
    onSuccess: invalidate,
  });

  const handleResolve = (id: string | null, note: string) => {
    if (id) resolveMutation.mutate({ id, note });
  };

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['open', 'resolved', 'all'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '5px 16px',
              borderRadius: 20,
              border: '1px solid',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              background:   tab === t ? 'var(--electric)' : 'white',
              color:        tab === t ? 'white'   : 'var(--gray-700)',
              borderColor:  tab === t ? 'var(--electric)' : 'var(--gray-200)',
            }}
          >
            {t === 'open' ? `Open (${openCount})` : t === 'resolved' ? 'Resolved' : 'All'}
          </button>
        ))}
      </div>

      {/* Cards */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Loading…</div>
      ) : filteredFlags.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--gray-500)', fontSize: 14 }}>
          {tab === 'open' ? '✓ No open flags' : 'No flags in this view'}
        </div>
      ) : (
        filteredFlags.map((f) => {
          const status      = st(f);
          const isOpen      = status === 'open';
          const isDismissed = status === 'dismissed';
          const contactName = (f as any).contact_name ?? 'Unknown Contact';

          const borderColor = isOpen ? 'var(--amber)' : status === 'resolved' ? 'var(--green)' : 'var(--gray-200)';

          return (
            <div
              key={f.id}
              style={{
                background:      'white',
                borderRadius:    7,
                border:          '1px solid var(--gray-200)',
                borderLeft:      `4px solid ${borderColor}`,
                overflow:        'hidden',
                marginBottom:    8,
                opacity:         isOpen ? 1 : 0.75,
              }}
            >
              {/* Top section */}
              <div style={{
                padding:       '9px 12px',
                display:       'flex',
                alignItems:    'flex-start',
                gap:           10,
                borderBottom:  '1px solid var(--gray-100)',
              }}>
                <Avatar name={contactName} size={26} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ fontSize: 12, fontWeight: 700, color: 'var(--nycem-blue)', cursor: 'pointer' }}
                    onClick={() => navigate(`/contacts/${f.contact_id}`)}
                  >
                    {contactName}
                  </div>
                  {((f as any).agency_acronym || (f as any).agency_name) && (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <AgencyLogo
                        name={(f as any).agency_name ?? (f as any).agency_acronym ?? ''}
                        acronym={(f as any).agency_acronym ?? undefined}
                        size={16}
                      />
                      {(f as any).agency_acronym ?? (f as any).agency_name}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  {/* Status pill */}
                  <span style={{
                    fontSize: 12,
                    fontWeight:   700,
                    padding:      '2px 8px',
                    borderRadius: 10,
                    background:   isOpen ? 'var(--amber-50)' : isDismissed ? 'var(--gray-100)' : 'var(--green-50)',
                    color:        isOpen ? 'var(--amber-text)' : isDismissed ? 'var(--gray-500)' : 'var(--green-700)',
                  }}>
                    {isOpen ? 'Open' : isDismissed ? 'Dismissed' : 'Resolved'}
                  </span>

                  {/* Action buttons — open flags, Superuser+ only */}
                  {isOpen && canResolve && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => dismissMutation.mutate(f.id)}
                        disabled={dismissMutation.isPending}
                        style={{
                          background:   'white',
                          color:        'var(--gray-500)',
                          border:       '1px solid var(--gray-200)',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight:   600,
                          padding:      '3px 10px',
                          cursor:       'pointer',
                        }}
                      >
                        Dismiss
                      </button>
                      <button
                        onClick={() => { setResolveModalFlagId(f.id); setResolutionNote(''); }}
                        style={{
                          background:   'var(--electric)',
                          color:        'white',
                          border:       'none',
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight:   600,
                          padding:      '3px 10px',
                          cursor:       'pointer',
                        }}
                      >
                        Resolve
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom section */}
              <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 12, flexShrink: 0, color: isOpen ? 'var(--amber-text)' : 'var(--green)' }}>
                  {isOpen ? '⚑' : '✓'}
                </span>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--gray-700)', lineHeight: 1.5 }}>
                    {(f as any).flag_reason}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
                    Raised by {(f as any).flagged_by_name ?? '—'} · {timeAgo(f.created_at ?? '')}
                  </div>
                  {f.resolution_note && (
                    <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-700)', fontStyle: 'italic' }}>
                      Resolution: {f.resolution_note}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })
      )}

      {/* Resolve modal */}
      {resolveModalFlagId && (
        <div style={{
          position:       'fixed',
          inset:          0,
          background:     'rgba(0,0,0,0.4)',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          zIndex:         1000,
        }}>
          <div style={{
            background:   'white',
            borderRadius: 10,
            padding:      24,
            width:        400,
            boxShadow:    '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nycem-blue)', marginBottom: 12 }}>
              Resolve Flag
            </div>
            <textarea
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              placeholder="Resolution note (optional)"
              style={{
                width:        '100%',
                height:       80,
                border:       '1px solid var(--gray-200)',
                borderRadius: 5,
                padding:      '8px 10px',
                fontSize:     12,
                resize:       'none',
                outline:      'none',
                marginBottom: 12,
                boxSizing:    'border-box',
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => { setResolveModalFlagId(null); setResolutionNote(''); }}
                style={{
                  background:   'white',
                  color:        'var(--gray-700)',
                  border:       '1px solid var(--gray-200)',
                  borderRadius: 5,
                  padding:      '6px 14px',
                  fontSize:     12,
                  fontWeight:   600,
                  cursor:       'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleResolve(resolveModalFlagId, resolutionNote)}
                disabled={resolveMutation.isPending}
                style={{
                  background:   'var(--electric)',
                  color:        'white',
                  border:       'none',
                  borderRadius: 5,
                  padding:      '6px 14px',
                  fontSize:     12,
                  fontWeight:   600,
                  cursor:       'pointer',
                }}
              >
                {resolveMutation.isPending ? 'Resolving…' : 'Confirm Resolve'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlagsPage;
